import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CaretDown, CalendarBlank, CheckCircle, Warning } from 'shared/icons';
import Badge from '../ui/Badge';
import Button from '../ui/Button';
import { Skeleton } from '../ui/Skeleton';
import { Sku } from '../ui/Mono';
import { useToast } from '../ui/Toast';
import api from '../../services/api';
import { FormatInt } from '../../utils/format';
import { FormatarDiaCurto } from '../../constants/coletas';

// ─────────────────────────────────────────────────────────────────────────────
// ENVIOS POR DIA — um accordion por dia de SLA ("despachar até").
//
// Unifica o que no zpl-checkout vive em três lugares (/cockpit/avancado: o que
// fazer hoje, o backlog e o "gerar lotes por dia SLA"): aqui é UMA lista, um
// dia por linha. Ontem e Hoje aparecem sempre; passado só com pendência;
// futuro só quando tem envio marcado — mesmo fim de semana, para dar
// programação. Dia 100% fica cinza, dado como concluído.
//
// O corpo do accordion responde "o que precisa sair nesse dia": produtos e
// quantidades ainda pendentes — carregado só quando o dia é aberto.
// ─────────────────────────────────────────────────────────────────────────────

const BadgeDoDia = ({ dia }) => {
    if (dia.meta === 0 && dia.pendentes === 0) {
        return <Badge variant="neutral">sem envios</Badge>;
    }
    const texto = `${FormatInt(dia.enviados)}/${FormatInt(dia.meta)} (${dia.percentual}%)`;
    if (dia.concluido) {
        return <Badge variant="neutral">{texto}</Badge>;
    }
    return <Badge variant={dia.atrasado ? 'danger' : dia.enviados > 0 ? 'warning' : 'default'}>{texto}</Badge>;
};

const CorpoDoDia = ({ dia, produtos, onVerEnvios }) => (
    <div className="space-y-3 border-t border-mono-100 p-4 dark:border-mono-900">
        {produtos === undefined ? (
            <Skeleton className="h-24 w-full" />
        ) : produtos.results?.length ? (
            <>
                <div className="flex flex-wrap items-center gap-2 text-xs text-mono-500 dark:text-mono-400">
                    <span>
                        Falta enviar: <strong>{FormatInt(produtos.resumo?.unidades)}</strong> unidade(s) de{' '}
                        <strong>{FormatInt(produtos.resumo?.skus)}</strong> produto(s)
                    </span>
                </div>
                <div className="max-h-80 overflow-y-auto rounded-lg border border-mono-200 dark:border-mono-800">
                    <table className="w-full text-sm">
                        <thead className="sticky top-0 bg-mono-50 dark:bg-mono-900">
                            <tr className="text-left text-xs uppercase tracking-wide text-mono-500">
                                <th className="whitespace-nowrap px-3 py-2 font-medium">SKU</th>
                                <th className="w-full max-w-0 px-3 py-2 font-medium">Produto</th>
                                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Unidades</th>
                                <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Envios</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-mono-100 dark:divide-mono-900">
                            {produtos.results.map((linha) => (
                                <tr key={linha.sku}>
                                    <td className="whitespace-nowrap px-3 py-2">
                                        <Sku value={linha.sku} className="text-xs" />
                                    </td>
                                    <td
                                        className="w-full max-w-0 truncate px-3 py-2 text-mono-600 dark:text-mono-400"
                                        title={linha.titulo || ''}
                                    >
                                        {linha.titulo || '—'}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums">
                                        {FormatInt(linha.unidades)}
                                    </td>
                                    <td className="whitespace-nowrap px-3 py-2 text-right font-mono tabular-nums text-mono-500">
                                        {FormatInt(linha.envios)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </>
        ) : (
            <p className="flex items-center gap-2 text-sm text-mono-500 dark:text-mono-400">
                <CheckCircle size={16} className="text-success-text" />
                Nada pendente para este dia — tudo enviado (ou cancelado).
            </p>
        )}
        <div className="flex justify-end">
            <Button variant="secondary" size="sm" onClick={onVerEnvios}>
                Ver listagem de envios do dia →
            </Button>
        </div>
    </div>
);

const EnviosAccordion = () => {
    const toast = useToast();
    const navigate = useNavigate();
    const [dados, setDados] = useState(null);
    const [carregando, setCarregando] = useState(true);
    const [abertos, setAbertos] = useState(() => new Set());
    const [produtosPorDia, setProdutosPorDia] = useState({});
    const [dataEscolhida, setDataEscolhida] = useState('');

    const carregar = useCallback(async () => {
        setCarregando(true);
        try {
            setDados(await api.getColetasEnviosDias());
        } catch (error) {
            toast.error(error?.message || 'Não foi possível carregar os dias de envio.');
        } finally {
            setCarregando(false);
        }
    }, [toast]);

    useEffect(() => {
        carregar();
    }, [carregar]);

    const alternarDia = useCallback(
        async (dia) => {
            setAbertos((atual) => {
                const proximo = new Set(atual);
                if (proximo.has(dia)) {
                    proximo.delete(dia);
                } else {
                    proximo.add(dia);
                }
                return proximo;
            });
            if (produtosPorDia[dia] === undefined) {
                try {
                    const resposta = await api.getColetasEnviosDiaProdutos(dia);
                    setProdutosPorDia((atual) => ({ ...atual, [dia]: resposta }));
                } catch (error) {
                    toast.error(error?.message || 'Não foi possível carregar os produtos do dia.');
                    setProdutosPorDia((atual) => ({ ...atual, [dia]: { results: [] } }));
                }
            }
        },
        [produtosPorDia, toast],
    );

    if (carregando) {
        return <Skeleton className="h-72 w-full" />;
    }

    if (dados && dados.habilitado === false) {
        return (
            <div className="rounded-xl border border-warning-200 bg-warning-soft p-4 text-sm text-warning-text dark:border-warning-900 dark:bg-warning-900/20">
                <Warning size={16} className="mr-1.5 inline" />
                {dados.motivo}
            </div>
        );
    }

    const dias = dados?.dias ?? [];

    return (
        <div className="space-y-4">
            <div className="flex flex-wrap items-end justify-between gap-3">
                <p className="max-w-[70ch] text-sm text-mono-600 dark:text-mono-400">
                    Um dia por prazo máximo de envio (SLA do ML). <strong>Ontem</strong> e <strong>Hoje</strong>{' '}
                    aparecem sempre; dias futuros, só quando têm envio marcado. Dia sem pendência fica cinza —
                    concluído.
                </p>
                {/* Pular direto para a listagem de uma data qualquer — o caso
                    "o motorista perguntou de anteontem". */}
                <div className="flex items-end gap-2">
                    <input
                        type="date"
                        value={dataEscolhida}
                        onChange={(evento) => setDataEscolhida(evento.target.value)}
                        className="rounded-lg border border-mono-300 bg-white px-3 py-2 text-sm text-black outline-none focus:border-black dark:border-mono-700 dark:bg-mono-900 dark:text-white dark:focus:border-white"
                    />
                    <Button
                        variant="secondary"
                        disabled={!dataEscolhida}
                        onClick={() => navigate(`/coletas/envios/${dataEscolhida}`)}
                    >
                        <CalendarBlank size={16} className="mr-1.5" />
                        Ver dia
                    </Button>
                </div>
            </div>

            {dias.length === 0 ? (
                <p className="rounded-xl border border-mono-200 p-6 text-center text-sm text-mono-500 dark:border-mono-800">
                    Nenhum dia com envios na janela de 30 dias.
                </p>
            ) : (
                <div className="space-y-2">
                    {dias.map((dia) => {
                        const aberto = abertos.has(dia.dia);
                        return (
                            <div
                                key={dia.dia}
                                className={`overflow-hidden rounded-xl border ${
                                    dia.concluido
                                        ? 'border-mono-200 bg-mono-50 opacity-70 dark:border-mono-800 dark:bg-mono-950'
                                        : dia.atrasado
                                          ? 'border-danger-300 bg-white dark:border-danger-900 dark:bg-mono-950'
                                          : 'border-mono-200 bg-white dark:border-mono-800 dark:bg-mono-950'
                                }`}
                            >
                                <button
                                    type="button"
                                    onClick={() => alternarDia(dia.dia)}
                                    className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-mono-50 dark:hover:bg-mono-900"
                                >
                                    <div className="flex flex-wrap items-center gap-3">
                                        <span
                                            className={`font-semibold ${
                                                dia.concluido
                                                    ? 'text-mono-500 dark:text-mono-400'
                                                    : 'text-black dark:text-white'
                                            }`}
                                        >
                                            {dia.rotulo || FormatarDiaCurto(dia.dia)}
                                        </span>
                                        {dia.rotulo ? (
                                            <span className="text-xs text-mono-400">{FormatarDiaCurto(dia.dia)}</span>
                                        ) : null}
                                        <BadgeDoDia dia={dia} />
                                        {dia.pendentes > 0 ? (
                                            <Badge variant={dia.atrasado ? 'danger' : 'warning'}>
                                                faltam {FormatInt(dia.pendentes)}
                                            </Badge>
                                        ) : null}
                                        {dia.concluido ? (
                                            <Badge variant="success">
                                                <CheckCircle size={14} className="mr-1 inline" />
                                                Concluído
                                            </Badge>
                                        ) : null}
                                        {dia.atrasado ? <Badge variant="danger">ATRASADO</Badge> : null}
                                        {dia.cancelados > 0 ? (
                                            <span className="text-[11px] text-mono-400">
                                                {FormatInt(dia.cancelados)} cancelado(s)
                                            </span>
                                        ) : null}
                                    </div>
                                    <CaretDown
                                        size={16}
                                        className={`shrink-0 text-mono-400 transition-transform ${aberto ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                {aberto ? (
                                    <CorpoDoDia
                                        dia={dia}
                                        produtos={produtosPorDia[dia.dia]}
                                        onVerEnvios={() => navigate(`/coletas/envios/${dia.dia}`)}
                                    />
                                ) : null}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

export default EnviosAccordion;
